import React from 'react'
import ScreenWrapper from '~/components/screen_wrappers/screen_wrapper'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { getAdviceProducts as getAdviceProductsSelector } from '@verzekeringapp-mono/shared/src/state/selectors/products'
import useProducts from '@verzekeringapp-mono/shared/src/hooks/use_product'
import { IAdviceProduct } from '@verzekeringapp-mono/shared/src/models/product'
import usePolicies from '@verzekeringapp-mono/shared/src/hooks/use_policies'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useForm } from 'react-hook-form'
import Button from '~/components/Button'
import { useThemeColorContext } from '~/providers/ThemeColorProvider'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'

const PolicyAdvice = () => {
  const products = useAppSelector(getAdviceProductsSelector)
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = React.useState(false)
  const { brandColors } = useThemeColorContext()
  const { getAdviceProducts } = useProducts()
  const { requestPolicyAdvise } = usePolicies()
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const { register, watch, setValue, handleSubmit } = useForm<{
    description: string
    selectedPolicies: string[]
  }>({
    defaultValues: {
      description: '',
      selectedPolicies: ['']
    }
  })
  const description = watch('description')
  const selectedPolicies = watch('selectedPolicies')
  const needExtraRow = selectedPolicies.length
  const showAddButton = needExtraRow && selectedPolicies.length < 10

  const setPolicyForIndex = (index: number, product: string) => {
    const newSelectedPolicies = [...selectedPolicies]

    newSelectedPolicies[index] = product

    setValue('selectedPolicies', newSelectedPolicies)
  }

  const deletePolicyAddIndex = (index: number) => {
    const newSelectedPolicies = selectedPolicies.filter((_, i) => i !== index)

    setValue('selectedPolicies', newSelectedPolicies)
  }

  const addPolicy = () => {
    const newSelectedPolicies = [...selectedPolicies, '']

    setValue('selectedPolicies', newSelectedPolicies)
  }

  React.useEffect(() => {
    getAdviceProducts()
  }, [])

  const submitAdviceRequest = async () => {
    setIsLoading(true)
    await requestPolicyAdvise(
      `${resourceAuthorization?.relationInfo?.relationNumber ?? ''}`,
      `${resourceAuthorization?.relationInfo?.agentNumber ?? ''}`,
      description,
      selectedPolicies
    ).finally(() => setIsLoading(false))

    navigate('/verzekeringen/advies-aangevraagd')
  }

  return (
    <ScreenWrapper hasPadding={false}>
      <form onSubmit={handleSubmit(submitAdviceRequest)}>
        <div className='flex flex-col items-center my-8 px-8 lg:w-2/3 w-full lg:mx-auto'>
          <p className='title mb-4'>Ik wil graag verzekeringsadvies</p>

          <p className='mb-8'>
            Graag ontvang ik advies over het afsluiten van aanvullende
            verzekeringen en/of dekkingen.
          </p>

          <ProductsSelector
            products={products}
            selectedPolicies={selectedPolicies}
            setPolicyForIndex={setPolicyForIndex}
            deletePolicyAddIndex={deletePolicyAddIndex}
          />

          {showAddButton && (
            <button
              className='mr-auto text-text-brand'
              onClick={addPolicy}
              type='button'
            >
              <FontAwesomeIcon
                icon='plus'
                className='mr-2 cursor-pointer'
                size='sm'
                color={brandColors.brand}
              />
              Verzekering toevoegen
            </button>
          )}

          <div className='flex flex-row justify-between w-full'>
            <p className='mr-auto mt-8 mb-2'>Mijn adviesvraag:</p>

            <p
              className={`text-right mt-8 mb-2 ${
                description.length >= 2000 ? 'text-status-danger-foreground' : ''
              }`}
            >
              {2000 - description.length}
            </p>
          </div>

          <textarea
            className='h-[200px] w-full rounded-md border border-border-default bg-surface-input p-4 focus:border-border-focus focus:ring-border-focus'
            placeholder='Vul hier zo veel mogelijke relevante informatie in...'
            {...register('description')}
            maxLength={2000}
          />

          <Button
            className={'primary-btn w-full mt-8'}
            disabled={
              description.length === 0 ||
              selectedPolicies.length === 0 ||
              selectedPolicies.includes('')
            }
            isLoading={isLoading}
          >
            Geef mij advies
          </Button>
        </div>
      </form>
    </ScreenWrapper>
  )
}

export default PolicyAdvice

const ProductsSelector: React.FC<{
  products: IAdviceProduct[]
  selectedPolicies: (string | undefined)[]
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setPolicyForIndex: (index: number, product: string) => void
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deletePolicyAddIndex: (index: number) => void
}> = ({
  products,
  selectedPolicies,
  setPolicyForIndex,
  deletePolicyAddIndex
}) => {
  const { brandColors } = useThemeColorContext()

  return (
    <>
      {selectedPolicies.map((selectedPolicy, index) => (
        <div
          className='mb-6 flex flex-row w-full flex-1 relative justify-center items-center'
          key={`policy-selector-${index}`}
        >
          <select
            className={
              'flex flex-1 rounded-md border border-border-default bg-surface-input p-4 shadow-sm focus:border-border-focus focus:ring-border-focus'
            }
            value={selectedPolicy}
            onChange={e => setPolicyForIndex(index, e.target.value)}
          >
            <option
              value=''
              disabled
              selected
              className='text-text-placeholder'
            >
              Selecteer een verzekering
            </option>

            {products && products.filter(p => p.name === selectedPolicy || !selectedPolicies.includes(p.name)).map(product => (
              <option
                key={`product-id-${product.id}`}
                value={product.name}
              >
                {product.name}
              </option>
            ))}
          </select>

          {index !== 0 && (
            <button
              type='button'
              onClick={() => deletePolicyAddIndex(index)}
              className='flex items-center justify-center w-[32px] cursor-pointer absolute -right-8'
            >
              <FontAwesomeIcon
                icon='trash'
                color={brandColors.brand}
                aria-label='Verwijder verzekering'
              />
            </button>
          )}
        </div>
      ))}
    </>
  )
}
